import {useElectricityQuery} from '@domain/schedule'
import {getUtcOffset} from '@hconnect/common/utils'
import {dataTestId} from '@hconnect/uikit'
import {dateFormatter, timeFormatter} from '@hconnect/uikit/src/common'
import {useUpmPlantTimezone, useUpmPlantContext} from '@hconnect/upm'
import {Button, Tooltip} from '@hm/fundamental'
import {mdiInformationOutline} from '@mdi/js'
import {Moment} from 'moment-timezone'
import {useTranslation} from 'react-i18next'

import {ENERGY_CONSUMPTION_CHART_WIDGET_NAMESPACE} from '../consts'
import {getSubmitTimeFrame} from '../utils/time'
import {adjustEnLocale} from '../utils/util'

interface EnergyProviderButtonProps {
  startOfPlan: Moment
  endOfPlan: Moment
}

export const EnergyProviderButton: React.FC<EnergyProviderButtonProps> = ({
  startOfPlan,
  endOfPlan
}) => {
  const {t, i18n} = useTranslation(ENERGY_CONSUMPTION_CHART_WIDGET_NAMESPACE)
  const {exists: translationExists, language} = i18n
  const timezoneId = useUpmPlantTimezone()
  const {plantId} = useUpmPlantContext()
  const {data: electricity} = useElectricityQuery({
    plantId,
    timezoneId,
    timeFrame: [startOfPlan, endOfPlan]
  })

  const [submitStart, submitEnd] = getSubmitTimeFrame(electricity?.upForPurchasing)

  const powerPurchaseInfoTranslationKey = `planning.powerPurchaseInfo.${plantId}`

  const formatDay = (date: Moment | undefined) =>
    date && dateFormatter(date, adjustEnLocale(language), getUtcOffset(timezoneId))
  const formatTime = (date: Moment | undefined) =>
    date && timeFormatter(date, language, getUtcOffset(timezoneId))
  const startDate = formatDay(submitStart)
  const startTime = formatTime(submitStart)
  const endDate = formatDay(submitEnd)
  const endTime = formatTime(submitEnd)

  if (!submitStart && !submitEnd) {
    return null
  }

  return (
    <div className="flex">
      <Tooltip
        content={`${t('planning.kpiToolTip', {startDate, startTime, endDate, endTime})}${
          translationExists(powerPurchaseInfoTranslationKey)
            ? `\n\n${t(powerPurchaseInfoTranslationKey)}`
            : ''
        }`}
      >
        <Button
          icon={mdiInformationOutline}
          variant="tertiary"
          className="text-primary_light"
          {...dataTestId('energy_provider_button')}
        >
          {t('planning.energyProvider')}
        </Button>
      </Tooltip>
    </div>
  )
}
